import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../../utils/cn';

const buttonVariants = cva(
  [
    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-full border font-medium whitespace-nowrap',
    'transition duration-150 motion-safe:active:scale-98',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4.5 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        default: 'border-stroke bg-primary text-primary-foreground hover:bg-primary-hover',
        destructive:
          'border-stroke bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border-stroke bg-surface-1 hover:bg-surface-2',
        secondary: 'border-stroke bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'border-transparent bg-transparent hover:bg-surface-2',
        // A dialog foot's Cancel: outlined in a sheet, where it stands as big as Save.
        dialogCancel:
          'border-transparent bg-transparent hover:bg-surface-2 in-data-sheet:border-stroke in-data-sheet:bg-surface-1',
        quiet: 'border-transparent bg-transparent text-fg-2 hover:bg-surface-2 hover:text-fg-1',
        link: 'border-transparent bg-transparent text-fg-1 hover:text-fg-2',
        linkMuted:
          'border-transparent text-muted-foreground underline-offset-4 hover:text-foreground hover:underline',
        linkAccent: 'border-transparent bg-transparent text-positive hover:underline',
        ink: 'border-fg-1 bg-fg-1 text-surface-1',
        inherit:
          'border-transparent bg-transparent text-inherit underline underline-offset-2 hover:opacity-80',
      },
      size: {
        sm: 'h-8 px-3 text-xs [&_svg]:size-4',
        default: 'h-9 px-3.5 text-sm',
        lg: 'h-11 px-5.5 text-base',
        xl: 'h-14 px-6.5 text-base font-bold',
        icon: 'h-9 w-9 p-0 text-sm',
        // A dialog foot's buttons: taller in a sheet, for the thumb.
        dialog: 'h-11 px-5.5 text-base in-data-sheet:h-12',
        tool: 'h-7.5 gap-0.5 pr-2 pl-2.5 text-xs [&_svg]:size-3.5',
        inline: 'h-auto border-0 p-0 text-xs',
      },
      isBlock: {
        true: 'w-full',
        false: '',
      },
    },
    // The inherit link drops the size scale whatever `size` says.
    compoundVariants: [
      {
        variant: 'inherit',
        className:
          'h-auto p-0 font-[weight:inherit] text-[length:inherit] motion-safe:active:scale-100',
      },
      // So does the accent link: a call to action set in text, with its own smaller icon.
      {
        variant: 'linkAccent',
        className:
          'h-auto gap-1.5 rounded-none border-0 p-0 text-sm font-semibold motion-safe:active:scale-100 [&_svg]:size-3.75',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      isBlock: false,
    },
  },
);

type ButtonVariants = VariantProps<typeof buttonVariants>;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Renders the single child in place of a `<button>` with its classes and
   * props, so a link can look like one.
   */
  asChild?: boolean;
  /**
   * Stretches the button to the full width of its container.
   */
  isBlock?: ButtonVariants['isBlock'];
  /**
   * `sm` 32px, `default` 36px, `lg` 44px, `xl` 56px; `icon` a 36px circle;
   * `tool` 30px, for a header's quiet text control such as See all; `inline`
   * sits in running text.
   */
  size?: ButtonVariants['size'];
  /**
   * `default` is the main action; `inherit` is a link taking its parent's
   * colour, size and weight.
   */
  variant?: ButtonVariants['variant'];
}

/**
 * The one button; a rendered `<button>` is `type="button"` unless the caller
 * says otherwise.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, isBlock, size, type, variant, ...props }, ref) => {
    const Root = asChild ? Slot : 'button';
    const buttonType = asChild ? type : (type ?? 'button');

    const classes = {
      root: cn(buttonVariants({ isBlock, size, variant }), className),
    };

    return (
      <Root
        className={classes.root}
        ref={ref}
        type={buttonType}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
